import { SHA256 } from "crypto-js";
import { IPromptUserDetails } from "@repo/tarsier-native-bridge/common-types";
import {
  getCookie,
  getSessionData,
  setCookie,
  unixEpoch,
  UserSessionDataTypes,
} from "@repo/shared-utils/common-utils";
import { encrypt, generateSalt } from "@repo/shared-utils/crypto";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";
import {
  // AIRTEL,
  CUST_ID,
  USER_DETAILS,
  USER_IDENTIFIER,
  uniqueDigits,
} from "../constants/sso.constants";
import { IResponse } from "./api";
import { IApiResponse } from "../services/demogApi";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { APP_M1, APP_SRC } from "@repo/shared-utils/sso.constants";

// Type for setSessionData and getSessionData functions
type sessionKeys =
  | "mobileNo"
  | "custType"
  | "custId"
  | "email"
  | "requestId"
  | "fullName"
  | "dob"
  | "firstName"
  | "lastName"
  | "isOffers"
  | "newDate"
  | "city"
  | "dealerId"
  | "cndFlag"
  | "dealerName"
  | "reKycFlag"
  | "kycRenewalDate"
  | "pinCode"
  | "indivCorpFlag"
  | "panNo"
  | "easyEmiProduct"
  | "rekycflag"
  | "kycProcess";

interface AppCustomerDemog {
  custType: string;
  description: string;
  statuscode: string;
  mobileNo: string;
  prospectID: string;
  custDemogDetails?: IPromptUserDetails;
}

export const getCustomerDemogFromApp = () => {
  const windowWithTarsier = window as Window & {
    tarsier?: { invokePrompt: (prompt: string, force?: boolean) => unknown };
  };
  const customerDemog: AppCustomerDemog = JSON.parse(
    windowWithTarsier.tarsier?.invokePrompt(APP_PROMPTS.CUSTOMER_DEMOG_DETAILS) as string
  );
  return customerDemog;
};

const parseSessionCookie = <T>(cookie: string) =>
  getCookie(cookie) ? (JSON.parse(getCookie(cookie) || "{}") as T) : null;

export const getSessionDataOrDefault = <T, K extends keyof T>(
  sessionObj: T | null,
  key: K,
  defaultValue: string | boolean
) => {
  // Prefer using nullish coalescing assignment for readability
  sessionObj ??= parseSessionCookie<T>("userDetails");
  return sessionObj?.[key] ?? defaultValue;
};

export function setSessionDataCookie(userDetails: UserSessionDataTypes) {
  setCookie(USER_DETAILS, 0, JSON.stringify(userDetails));
}

// Set session Data using key | value
export const setSessionData = async (
  key: sessionKeys,
  value: string | boolean,
  saltFromCaller?: string
): Promise<void> => {
  const sessionObj = parseSessionCookie<UserSessionDataTypes>("userDetails");

  let userDetails = {
    newDate: getSessionDataOrDefault(sessionObj, "newDate", ""),
    custType: getSessionDataOrDefault(sessionObj, "custType", ""),
    custId: getSessionDataOrDefault(sessionObj, "custId", ""),
    mobileNo: getSessionDataOrDefault(sessionObj, "mobileNo", ""),
    fullName: getSessionDataOrDefault(sessionObj, "fullName", ""),
    dob: getSessionDataOrDefault(sessionObj, "dob", ""),
    firstName: getSessionDataOrDefault(sessionObj, "firstName", ""),
    lastName: getSessionDataOrDefault(sessionObj, "lastName", ""),
    requestId: getSessionDataOrDefault(sessionObj, "requestId", ""),
    email: getSessionDataOrDefault(sessionObj, "email", ""),
    isOffers: getSessionDataOrDefault(sessionObj, "isOffers", ""),
    city: getSessionDataOrDefault(sessionObj, "city", ""),
    dealerId: getSessionDataOrDefault(sessionObj, "dealerId", ""),
    dealerName: getSessionDataOrDefault(sessionObj, "dealerName", ""),
    cndFlag: getSessionDataOrDefault(sessionObj, "cndFlag", false),
    reKycFlag: getSessionDataOrDefault(sessionObj, "reKycFlag", ""),
    kycRenewalDate: getSessionDataOrDefault(sessionObj, "kycRenewalDate", ""),
    pinCode: getSessionDataOrDefault(sessionObj, "pinCode", ""),
    indivCorpFlag: getSessionDataOrDefault(sessionObj, "indivCorpFlag", ""),
    easyEmiProduct: getSessionDataOrDefault(sessionObj, "easyEmiProduct", ""),
    rekycflag: getSessionDataOrDefault(sessionObj, "rekycflag", ""),
    kycProcess: getSessionDataOrDefault(sessionObj, "kycProcess", ""),
  };

  const needsEncryption = ["mobileNo", "dob", "email", "panNo"].includes(key);
  if (needsEncryption && value) {
    const salt: string =
      saltFromCaller || (userDetails.newDate as string) || (await generateSalt());

    const encryptedValue = await encrypt(JSON.stringify(value), salt);

    if (key === "mobileNo") {
      uniqueDigits.uniqueDigits = value as string;
    }

    userDetails = {
      ...userDetails,
      [key]: encryptedValue,
      newDate: salt, // ensure we persist the salt if not already present
    };
  } else {
    userDetails = {
      ...userDetails,
      [key]: value,
    };
  }

  setSessionDataCookie(userDetails as UserSessionDataTypes);
};

export type PerpetualSessionKeys =
  | "mobileNo"
  | "fullName"
  | "firstName"
  | "custType"
  | "custId"
  | "newDate";

export type PerpetualSessionDataType = Pick<UserSessionDataTypes, PerpetualSessionKeys>;

export const setPerpetualSessionData = async (
  key: PerpetualSessionKeys,
  value: string
): Promise<void> => {
  await setSessionData(key, value);
};

export const getPerpetualSessionData = async (): Promise<PerpetualSessionDataType> => {
  return await getSessionData();
};

export const updateCleverTapPageObject = (params: Record<string, string>) => {
  if ((window as any).clevertap) {
    (window as any).clevertap.ctPageObject = {
      ...(window as any).clevertap.ctPageObject,
      ...params,
    };
  }
};
export const isValidJSON = (json: string | null): boolean => {
  if (!json) return false;
  try {
    JSON.parse(json);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const isValidURL = (url: string) => {
  try {
    return Boolean(new URL(url));
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const getFullUrl = (url: string, options = { hash: true }): string => {
  if (!isValidURL(url)) {
    const currentUrl = new URL((window as any).location.href);
    let newUrl: string | URL = new URL(`${currentUrl.protocol}//${currentUrl.hostname}${url}`);
    if (!options.hash) {
      return newUrl.toString().replace(newUrl.hash, "");
    }
    const src = getParams(APP_SRC);
    const separator = newUrl.toString().includes("?") ? "&" : "?";
    const quesrParam = `source=${src}`;
    newUrl = src === APP_M1 ? `${newUrl}${separator}${quesrParam}` : newUrl;
    return newUrl.toString();
  }
  return url;
};

export const stringToBoolean = (value: string | boolean | undefined): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (value === null || value === undefined) {
    return false;
  }
  switch (String(value)?.toLowerCase().trim()) {
    case "true":
    case "yes":
    case "1":
      return true;
    case "false":
    case "no":
    case "0":
    case null:
      return false;
    default:
      return Boolean(value);
  }
};

export const getNearestAnchorElement = (element: HTMLElement): HTMLAnchorElement | null => {
  if (element.tagName === "A") {
    return element as HTMLAnchorElement;
  }
  const anchorElement = element.closest("a");
  if (anchorElement) {
    return anchorElement;
  }
  return null;
};

export const objKeyUpdate = <T>(data: { [key: string]: T }): (T | undefined)[] => {
  if (!data) return [];
  const keys = Object.keys(data)
    .map((k) => +k.replace("item", ""))
    .sort((a, b) => a - b);
  const values = keys.map((key) => data[`item${key}`]);
  return values;
};

export const retry = async <
  // biome-ignore lint/suspicious/noExplicitAny: cannot know type of args AOT
  Fn extends (...args: any[]) => any,
>(
  fn: Fn,
  retriesLeft = 5,
  interval = 500,
  ...args: Parameters<Fn>
): Promise<ReturnType<Fn>> => {
  try {
    const result = await fn(...args);
    return result;
  } catch (error) {
    if (retriesLeft) {
      if (interval !== 0) await new Promise((r) => setTimeout(r, interval));
      return retry(fn, retriesLeft - 1, interval, ...args);
    }
    throw error;
  }
};

export const debounce = <
  // biome-ignore lint/suspicious/noExplicitAny: cannot know type of args AOT
  Fn extends (...args: any[]) => any,
>(
  fn: Fn,
  delay: number
) => {
  let timerId: ReturnType<typeof setTimeout>;
  return (newDelay?: number, after?: () => void, ...args: Parameters<Fn>) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      fn(...args);
      if (after) after();
    }, newDelay ?? delay);
  };
};

export const throttle = <
  // biome-ignore lint/suspicious/noExplicitAny: cannot know type of args AOT
  Fn extends (...args: any[]) => any,
>(
  fn: Fn,
  delay: number
) => {
  let timerId: ReturnType<typeof setTimeout> | undefined;
  return (...args: Parameters<Fn>) => {
    if (timerId) return;
    timerId = setTimeout(() => {
      fn(...args);
      timerId = undefined;
    }, delay);
  };
};

export const displayNumberIndianFormat = (n: number) => {
  let number = Intl.NumberFormat("en-IN").format(n);
  number = number.split(".")[1]?.length === 1 ? `${number}0` : number;
  return number;
};

export const intToStringAccurate = (val: number): string => {
  let formattedValue;

  if (val >= 10000000) {
    if (+val % 10000000 === 0) {
      formattedValue = `${Math.trunc(+val / 10000000)} Cr`;
    } else {
      formattedValue = `${(+val / 10000000).toFixed(2)} Cr`;
    }
  } else if (val >= 100000) {
    if (+val % 100000 === 0) {
      formattedValue = `${Math.trunc(+val / 100000)} L`;
    } else {
      formattedValue = `${(+val / 100000).toFixed(2)} L`;
    }
  } else if (val >= 1000) {
    formattedValue = `${Math.trunc(+val / 1000)} K`;
  } else {
    formattedValue = val.toString();
  }

  return formattedValue;
};

export const stringToInt = (val: string): number => {
  if (typeof val === "number") return val;
  if (val === null || val === undefined) return 0;

  const intVal = +(val?.split(" ")[0] ?? "0");

  if (val.includes("Cr")) return intVal * 10000000;
  if (val.includes("L")) return intVal * 100000;
  if (val.includes("K")) return intVal * 1000;

  return +val;
};

export const getParams = (paramsValue: string): string | null => {
  const url = new URL((window as any).location.href);
  const params = url.searchParams;
  return params.get(paramsValue);
};

export const generateID = (idLength: number): string => {
  let generatedID = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const charactersLength = characters.length;
  for (let i = 0; i < idLength; i++) {
    generatedID += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return generatedID;
};

export const appendQueryParams = (
  baseUrl: string,
  queryParams: Record<string, string | number>
): string => {
  const url = new URL(baseUrl);
  for (const [key, value] of Object.entries(queryParams)) {
    url.searchParams.append(key, String(value));
  }
  return url.toString();
};

export const addQueryParamsWithVanity = (
  baseString: string,
  params: Record<string, string | number>
): string => {
  if (!params || Object.keys(params).length === 0) {
    return baseString;
  }
  const hasQueryParams = baseString.includes("?");
  const resultString = baseString + (hasQueryParams ? "&" : "?");
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      const encodedKey = encodeURIComponent(key);
      const encodedValue = encodeURIComponent(value.toString());
      return `${encodedKey}=${encodedValue}`;
    })
    .join("&");

  return resultString + queryString;
};

// Truncate a string to a certain count
export const stringTruncator = (text: string, count: number) => {
  return text.slice(0, count);
};

export const isValidPhoneNumber = (phoneNumber: string) => /^[6-9]\d{9}$/.test(phoneNumber);
/**
 * Creates a timer utility that can be used to measure elapsed time.
 * @returns {Object} Timer object with methods to start, end, and measure time.
 * @property {function} start - Starts the timer by recording the current time.
 * @property {function} end - Ends the timer by recording the current time.
 * @property {function} measure - Measures the elapsed time between start and end.
 * @throws Will throw an error if `start` or `end` is called out of order.
 */
export const createTimer = () => {
  let startTime: number;
  let stopTime: number;
  return {
    start() {
      startTime = +unixEpoch();
    },
    end() {
      stopTime = +unixEpoch();
    },
    /**
     * Measures the elapsed time between start and end.
     * @returns {number} Elapsed time in milliseconds.
     * @throws Will throw an error if `start` and `end` are not called in order.
     */
    measure(): number {
      if (typeof startTime === "undefined" || typeof stopTime === "undefined") {
        throw new Error("Timer must be started and ended before measuring.");
      }
      return stopTime - startTime;
    },
  };
};

export const conditionalValue = <T, U>(condition: boolean, trueValue: T, falseValue: U): T | U =>
  condition ? trueValue : falseValue;

export const limitCharacters = (text: string, limit: number): string => {
  if (text.length > limit) {
    if (text.endsWith(".")) return `${text.substring(0, limit - 1).trim()}...`;
    return `${text.substring(0, limit).trim()}...`;
  }
  return text;
};

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

interface InstalledRelatedApp {
  id?: string;
  platform?: string;
  url?: string;
}

// Function to check if PWA is already installed on user's device
export const checkIsAppInstalled = async (): Promise<boolean> => {
  let isAppInstalled = false;
  const navigatorWithRelatedApps = navigator as Navigator & {
    getInstalledRelatedApps?(): Promise<InstalledRelatedApp[]>;
  };
  try {
    if (navigatorWithRelatedApps.getInstalledRelatedApps) {
      const relatedApps: InstalledRelatedApp[] =
        await navigatorWithRelatedApps?.getInstalledRelatedApps();
      isAppInstalled = relatedApps.length > 0 && relatedApps.some((app) => app.platform === "play");
    } else {
      isAppInstalled = false;
    }
  } catch (error) {
    console.error("Failed to check if app is installed:", error);
    isAppInstalled = false;
  }
  return isAppInstalled;
};
// get query parameters from the given URL
export const getUrlQueryParams = (href: string) => {
  const url = new URL(href, (window as any).location.origin);
  const queryParams = new URLSearchParams(url.search);
  const values: {
    [key: string]: string;
  } = {};
  for (const [key, value] of queryParams.entries()) {
    values[key] = value;
  }
  return values;
};

// remove query parameters from a given URL
export const removeParamsFromUrl = (href: string) => {
  const url = new URL(href, (window as any).location.origin);
  url.search = ""; // Remove query parameters
  return url.toString();
};

// add utm values for a link
export const addUTMValues = (link: string) => {
  let updatedUrl = "";
  const urlObj = new URL(link, (window as any).location.origin);
  const searchParams = urlObj.searchParams;
  const isUtmSourceCorrect = searchParams.get("utm_source") === "organic_bfl";
  const isUtmMediumCorrect = searchParams.get("utm_medium") === "Investments";
  const ogUrlMetaTag = document.querySelector('meta[property="og:url"]');
  const ogUrlContent = ogUrlMetaTag?.getAttribute("content") ?? "";
  if (ogUrlContent && isUtmSourceCorrect && isUtmMediumCorrect) {
    // add new `utm_campaign` parameter
    urlObj.searchParams.append("utm_campaign", ogUrlContent.replace(/^\//, ""));
    // update the URL
    updatedUrl = urlObj.toString();
  }
  return updatedUrl;
};

export const getDataOrFallBack = (data: string | undefined | null, fallback: string) =>
  data || fallback;

/**
 * Returns a formatted date string based on the provided date string or the current date.
 *
 * @returns {string} The formatted date string (e.g., 11 December 2024, 11 Dec 2024).
 */
export const getCustomDate = (dateString = "", shortMonth = false) => {
  const date = dateString ? new Date(dateString.split("/").reverse().join("-")) : new Date();
  const monthName = date.toLocaleString("default", {
    month: shortMonth ? "short" : "long",
  });
  return `${date.getDate()} ${monthName} ${date.getFullYear()}`;
};

export const isTimeExceeded = (leadCreationDate: number) => {
  const tenDaysInSeconds = 10 * 24 * 60 * 60;
  const futureTime = leadCreationDate + tenDaysInSeconds;
  const currentEpochTime = Math.floor(Date.now() / 1000);
  return futureTime <= currentEpochTime;
};

export const validIncomeRange = (value: number, minimun: number, maximum: number) => {
  return value >= minimun && value <= maximum;
};

export const personalDetailsFormDateFormat = (inputDate: string) => {
  const [year, month, day] = inputDate.split("-");
  return `${day}/${month}/${year}`;
};

/**
 * Constructs a full URL path by appending the provided path to the current domain.
 * If the current URL includes 'app-v', it is prepended to the path.
 *
 * @param {string} path The path to be appended to the current domain.
 * @returns {string} The fully constructed URL (e.g., https://example.com/app-v1/your-path).
 */

export const generateUrlPath = (path: string) => {
  const currentDomain = (window as any).location.origin;
  const currentHref = (window as any).location.href;

  const versionMatch = currentHref?.match(/app-v(\d+)/);
  const version = versionMatch ? versionMatch[0] : null;

  const formattedPath = version ? `${version}/${path}` : path;

  return `${currentDomain}/${formattedPath}`;
};

export const removeAnchorTags = (element: Element) => {
  const anchorTags = element.querySelectorAll("a");
  for (const anchor of anchorTags) {
    if (anchor.hasAttribute("class")) {
      continue;
    }
    const parent = anchor.parentNode;
    if (parent) {
      while (anchor.firstChild) {
        parent.insertBefore(anchor.firstChild, anchor);
      }
      parent.removeChild(anchor);
    }
  }
};

export const removeAnchorTagsIfOpenInApp = (element: Element) => {
  if (isOpenInApp()) {
    removeAnchorTags(element);
  }
};

const sanitizePromptUserDetails = (
  details: Partial<IPromptUserDetails> | null | undefined
): IPromptUserDetails => {
  const sanitizedDetails: Record<keyof IPromptUserDetails, string> = {} as Record<
    keyof IPromptUserDetails,
    string
  >;
  const validDetails = details && typeof details === "object" ? details : {};

  for (const key of Object.keys(validDetails) as (keyof IPromptUserDetails)[]) {
    const value = validDetails[key];
    sanitizedDetails[key] = value ?? "";
  }

  for (const key of Object.keys(sanitizedDetails) as (keyof IPromptUserDetails)[]) {
    if (!(key in validDetails)) {
      sanitizedDetails[key] = "";
    }
  }

  return sanitizedDetails;
};

export const getDemogPromptData = (demogData: AppCustomerDemog): IResponse<IApiResponse> => {
  const demogInfo =
    Array.isArray(demogData?.custDemogDetails) && demogData.custDemogDetails.length > 0
      ? demogData.custDemogDetails[0]
      : {};

  const details = sanitizePromptUserDetails(demogInfo);

  const demogDetails = {
    custType: demogData?.custType,
    city: details?.cityName,
    firstName: details?.firstName,
    lastName: details?.lastName,
    gender: details?.gender,
    employmentType: details?.employmentType,
    annualIncome: details?.annualIncome,
    custId: details?.id,
    addressLine1: details?.addressLine1,
    addressLine2: details?.addressLine2,
    addressType: details?.addressType,
    stateName: details?.stateName,
    pinCode: details?.pinCode,
    reKycFlag: details?.rekycFlag || details?.rekycflag,
    kycRenewalDate: details?.kycrenewaldate,
    indivCorpFlag: details?.indivCorpFlag,
    panNo: details?.panNo,
    dob: details?.dob,
    gstnNumber: details?.gstnNumber,
    email: details?.email,
    kycProcess: details?.kycProcess,
    rekycflag: details?.rekycflag,
    multipleCustIds: [],
  };

  return {
    statusCode: 90,
    status: "success",
    message: "success",
    data: demogDetails,
  };
};

export const setDemogStorage = async (userData: IResponse<IApiResponse>, mobileNo: string) => {
  const firstName = getDataOrFallBack(userData.data?.firstName, "");
  const lastName = getDataOrFallBack(userData.data?.lastName, "");
  const fullName = `${firstName} ${lastName}`.trim();

  // Generate a single salt for this session
  const salt = await generateSalt();

  // Set the salt for encryption use
  await setSessionData("newDate", salt);

  // Set mobileNo and dob separately since they need encryption
  await setSessionData("mobileNo", mobileNo, salt);
  await setPerpetualSessionData("mobileNo", mobileNo);
  await setSessionData("dob", userData?.data?.dob, salt);

  // Set rest of the session data in parallel
  await Promise.all([
    setSessionData("city", userData?.data?.city),
    setSessionData("custId", userData?.data?.custId),
    setSessionData("custType", userData?.data?.custType),
    setSessionData("reKycFlag", userData?.data?.reKycFlag),
    setSessionData("kycRenewalDate", userData?.data?.kycRenewalDate),
    setSessionData("firstName", firstName),
    setSessionData("fullName", fullName),
    setSessionData("lastName", lastName),
    setSessionData("pinCode", `${userData?.data?.pinCode}`),
    setSessionData("indivCorpFlag", `${userData?.data?.indivCorpFlag}`),
    setSessionData("rekycflag", userData?.data?.rekycflag || ""),
    setSessionData("kycProcess", userData?.data?.kycProcess || ""),
    setPerpetualSessionData("custType", userData?.data.custType),
    setPerpetualSessionData("fullName", fullName),
    setPerpetualSessionData("firstName", firstName),
  ]);

  // For Adobe target
  localStorage.setItem(USER_IDENTIFIER, SHA256(mobileNo).toString());
  localStorage.setItem(CUST_ID, userData?.data?.custId);
};

export function removeQueryParam(url: string, parameterName: string) {
  const urlObj = new URL(url);
  const searchParams = urlObj.searchParams;

  if (searchParams.has(parameterName)) {
    searchParams.delete(parameterName);
    urlObj.search = searchParams.toString();
    (window as any).history.replaceState({}, "", urlObj);
  }
}
